import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import api from "../api/client";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "logged_out", label: "Logged Out" },
  { value: "forced_logout", label: "Forced Logout" },
];

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700",
  logged_out: "bg-gray-100 text-gray-600",
  forced_logout: "bg-red-100 text-red-700",
};

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getDefaultDateFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split("T")[0];
}

function getToday() {
  return new Date().toISOString().split("T")[0];
}

export default function LoginActivityPage() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();
  const canManage = hasPerm("sessions.manage");

  // Summary
  const [summary, setSummary] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState(getToday());

  // Table data
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  // Roles for filter dropdown
  const [roles, setRoles] = useState([]);

  const [roleFilter, setRoleFilter] = useState("");

  // Detail modal
  const [selectedSession, setSelectedSession] = useState(null);

  // Fetch roles once
  useEffect(() => {
    api.get("/roles").then((r) => setRoles(r.data || [])).catch(() => {});
  }, []);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/sessions/summary");
      setSummary(data);
    } catch { /* ignore */ }
  }, []);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/sessions", { params });
      setSessions(data.items || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, pageSize, search, statusFilter, roleFilter, dateFrom, dateTo]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [statusFilter, roleFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleForceLogout = async (sessionId) => {
    if (!(await confirm({
      title: "Force Logout",
      message: "Are you sure you want to force-logout this session?",
      confirmLabel: "Force Logout",
      variant: "danger",
    }))) return;
    try {
      await api.post(`/sessions/${sessionId}/force-logout`);
      fetchSessions();
      fetchSummary();
      setSelectedSession(null);
    } catch {
      // silently fail
    }
  };

  const handleRefresh = () => { fetchSessions(); fetchSummary(); };

  const Nav = isMobile ? MobileNavbar : Navbar;

  return (
    <div className="min-h-screen bg-gray-50">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Login Activity</h1>
          <button onClick={handleRefresh} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Active Sessions" value={summary.active_sessions} color="green" />
            <SummaryCard label="Logins Today" value={summary.logins_today} color="blue" />
            <SummaryCard label="Forced Logouts Today" value={summary.forced_logouts_today} color="red" />
            <SummaryCard label="Unique Users Today" value={summary.unique_users_today} color="purple" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input
              type="text"
              placeholder="Search name or username…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Login Time</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Logout Time</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Duration</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">IP</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Device</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Last Seen</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">End Reason</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-12 text-gray-400">Loading…</td></tr>
                ) : sessions.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-gray-400">No sessions found.</td></tr>
                ) : (
                  sessions.map((s, i) => (
                    <tr
                      key={s.id}
                      className={`border-b hover:bg-blue-50 cursor-pointer ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                      onClick={() => setSelectedSession(s)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-900">{s.user_full_name}</div>
                        <div className="text-xs text-gray-500">{s.username}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{s.role_name || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-600">{formatDateTime(s.login_at)}</td>
                      <td className="px-4 py-2.5 text-gray-600">{formatDateTime(s.logout_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || "bg-gray-100 text-gray-600"}`}>
                          {s.status === "active" ? "Active" : s.status === "logged_out" ? "Logged Out" : s.status === "forced_logout" ? "Forced Logout" : s.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{formatDuration(s.duration_seconds)}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs font-mono">{s.ip_address || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs hidden lg:table-cell">{[s.browser, s.os].filter(Boolean).join(" / ") || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs hidden lg:table-cell">{formatDateTime(s.last_seen_at)}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{s.end_reason?.replace(/_/g, " ") || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-600">
              Showing {sessions.length} of {total} sessions
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSelectedSession(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Session Details</h3>
              <button onClick={() => setSelectedSession(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <DetailRow label="User" value={selectedSession.user_full_name} />
              <DetailRow label="Username" value={selectedSession.username} />
              <DetailRow label="Role" value={selectedSession.role_name} />
              <DetailRow label="Status" value={selectedSession.status?.replace(/_/g, " ")} />
              <DetailRow label="Login Time" value={formatDateTime(selectedSession.login_at)} />
              <DetailRow label="Logout Time" value={formatDateTime(selectedSession.logout_at)} />
              <DetailRow label="Last Seen" value={formatDateTime(selectedSession.last_seen_at)} />
              <DetailRow label="Duration" value={formatDuration(selectedSession.duration_seconds)} />
              <DetailRow label="IP Address" value={selectedSession.ip_address} />
              <DetailRow label="Browser" value={selectedSession.browser} />
              <DetailRow label="OS" value={selectedSession.os} />
              <DetailRow label="Device Type" value={selectedSession.device_type} />
              <DetailRow label="Session ID" value={selectedSession.session_token_short} />
              <DetailRow label="End Reason" value={selectedSession.end_reason?.replace(/_/g, " ")} />
              {selectedSession.forced_by_name && (
                <DetailRow label="Forced By" value={selectedSession.forced_by_name} />
              )}
            </div>
            {canManage && selectedSession.status === "active" && (
              <div className="px-6 py-4 border-t">
                <button
                  onClick={() => handleForceLogout(selectedSession.id)}
                  className="w-full py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Force Logout This Session
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const colors = {
    green: "bg-green-50 border-green-200 text-green-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    red: "bg-red-50 border-red-200 text-red-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right">{value || "—"}</span>
    </div>
  );
}
